import { MIN_CL, MAX_CL } from "codes";
import { isPartnerDetailsComplete } from "components/Company/Lead/utils/reverseFactoringFlow";
import { BuyerAdditionalDetailsDefaults } from "helpers/formFields/tradingRelationship";
import type { InviteTradePartners, Partner } from "types";

const toBooleanFlag = (value: unknown) => value === true || value === "true";

const toFiniteNumber = (value: unknown, fallback = 0) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : fallback;
};

export const isSupplierPartnerClAmountValid = (
  partners: Array<Pick<Partner, "requested_amount">> | null | undefined,
) =>
  Boolean(partners?.length) &&
  (partners ?? []).every((partner) => {
    const amount = partner.requested_amount;
    return typeof amount === "number" && amount >= MIN_CL && amount <= MAX_CL;
  });

export const getSupplierCreditLimitInvitePayload = (
  companyId?: string | null,
  partners?: Partner[] | null,
): InviteTradePartners | null => {
  if (!companyId || !isSupplierPartnerClAmountValid(partners)) {
    return null;
  }
  return {
    company_id: companyId,
    data: (partners ?? []).map((partner) => ({
      ...partner,
      address_line1: partner.address_line1 ?? "",
      city: partner.city ?? "",
      postcode: partner.postcode ?? "",
      iso3: partner.iso3 ?? "",
      is_identified: toBooleanFlag(partner.is_identified),
      annual_revenue: toFiniteNumber(partner.annual_revenue),
      number_of_past_paid_transaction: toFiniteNumber(
        partner.number_of_past_paid_transaction,
      ),
      average_invoice_amount: toFiniteNumber(partner.average_invoice_amount),
      average_payment_terms: partner.average_payment_terms ?? "",
      any_delays_of_payment_with_buyer: toBooleanFlag(
        partner.any_delays_of_payment_with_buyer,
      ),
      reason_of_any_delays_of_payment_with_buyer:
        partner.reason_of_any_delays_of_payment_with_buyer ?? "",
      any_credit_note: toBooleanFlag(partner.any_credit_note),
      reason_for_credit_note: partner.reason_for_credit_note ?? "",
      industry: partner.industry ?? "",
      goods_category: partner.goods_category ?? "",
    })),
  };
};

export const canContinuePresalesPartnerStep = (partners?: Partner[] | null) =>
  Boolean(partners?.length) &&
  (partners ?? []).every((partner) => isPartnerDetailsComplete(partner));

const hasFilledValue = (value: unknown) =>
  value !== undefined && value !== null && value !== "";

const overlaySavedPartnerDetails = (
  apiPartner: Partner,
  savedPartner: Partner,
): Partner => ({
  ...apiPartner,
  ...Object.fromEntries(
    Object.entries(savedPartner).filter(
      ([, value]) => hasFilledValue(value) || value === false || value === 0,
    ),
  ),
  persistedFromApi:
    apiPartner.persistedFromApi || savedPartner.persistedFromApi,
});

export const mergePresalesPartnerLists = (
  saved: Partner[] | null | undefined,
  api: Partner[] | null | undefined,
): Partner[] => {
  const savedList = saved ?? [];
  const apiList = api ?? [];
  if (!savedList.length) {
    return apiList;
  }
  if (!apiList.length) {
    return savedList;
  }

  const apiByReg = new Map(
    apiList.map((partner) => [partner.registration_number, partner]),
  );
  const mergedSaved = savedList.map((savedPartner) => {
    const apiPartner = apiByReg.get(savedPartner.registration_number);
    return apiPartner
      ? overlaySavedPartnerDetails(apiPartner, savedPartner)
      : savedPartner;
  });
  const savedRegs = new Set(
    savedList.map((partner) => partner.registration_number),
  );
  return [
    ...mergedSaved,
    ...apiList.filter((partner) => !savedRegs.has(partner.registration_number)),
  ];
};

export const getBuyerAdditionalDetailsInitialValues = (
  item?: Partner | null,
) => {
  if (!item) {
    return { ...BuyerAdditionalDetailsDefaults };
  }

  return {
    ...BuyerAdditionalDetailsDefaults,
    address_line1: item.address_line1 ?? "",
    city: item.city ?? "",
    postcode: item.postcode ?? "",
    industry: item.industry ?? "",
    goods_category: item.goods_category ?? "",
    annual_revenue:
      item.annual_revenue === undefined || item.annual_revenue === null
        ? BuyerAdditionalDetailsDefaults.annual_revenue
        : String(item.annual_revenue),
    number_of_past_paid_transaction:
      item.number_of_past_paid_transaction ??
      BuyerAdditionalDetailsDefaults.number_of_past_paid_transaction,
    average_invoice_amount:
      item.average_invoice_amount === undefined ||
      item.average_invoice_amount === null
        ? BuyerAdditionalDetailsDefaults.average_invoice_amount
        : String(item.average_invoice_amount),
    average_payment_terms: item.average_payment_terms ?? "",
    any_credit_note: toBooleanFlag(item.any_credit_note),
    any_delays_of_payment_with_buyer: toBooleanFlag(
      item.any_delays_of_payment_with_buyer,
    ),
    reason_for_credit_note: item.reason_for_credit_note ?? "",
    reason_of_any_delays_of_payment_with_buyer:
      item.reason_of_any_delays_of_payment_with_buyer ?? "",
  };
};

type StepsForm = {
  list?: Partner[] | null;
  [key: string]: unknown;
};

type StepsPayload = {
  step?: number;
  form?: StepsForm | null;
};

const isStepsPayload = (value: unknown): value is StepsPayload =>
  Boolean(value) &&
  typeof value === "object" &&
  ("step" in (value as object) || "form" in (value as object));

export const unwrapStepsPayload = <TForm extends { list?: Partner[] | null }>(
  response: unknown,
  fallback: { step: number; form: TForm },
): { step: number; form: TForm } => {
  const wrapped =
    response &&
    typeof response === "object" &&
    "data" in response &&
    isStepsPayload((response as { data?: unknown }).data)
      ? ((response as { data: StepsPayload }).data as StepsPayload)
      : isStepsPayload(response)
        ? response
        : undefined;

  const step = typeof wrapped?.step === "number" ? wrapped.step : fallback.step;
  const responseForm =
    wrapped?.form && typeof wrapped.form === "object" ? wrapped.form : {};
  const responseList = Array.isArray(responseForm.list)
    ? responseForm.list
    : [];
  const fallbackList = fallback.form.list ?? [];
  const list = mergePresalesPartnerLists(fallbackList, responseList);

  return {
    step,
    form: {
      ...fallback.form,
      ...responseForm,
      list,
    },
  };
};
